import React from 'react';
import styled from 'styled-components';
import MoonLineIcon from 'remixicon-react/MoonLineIcon';
import SunLineIcon from 'remixicon-react/SunLineIcon';
import useThemeMode from '../../../services/hooks/useThemeMode';

interface ThemeToggleProps {
    showLabel?: boolean;
    className?: string;
}

const ThemeToggle: React.FC<ThemeToggleProps> = ({ showLabel = false, className }) => {
    const { isDarkMode, toggle } = useThemeMode();

    return (
        <Container
            className={className}
            onClick={toggle}
            title={isDarkMode ? 'Switch to light mode' : 'Switch to dark mode'}
        >
            <ToggleTrack $isDark={isDarkMode}>
                <IconWrapper $position="left" $isActive={!isDarkMode}>
                    <SunLineIcon size={12} />
                </IconWrapper>
                <IconWrapper $position="right" $isActive={isDarkMode}>
                    <MoonLineIcon size={12} />
                </IconWrapper>
                <ToggleThumb $isDark={isDarkMode} />
            </ToggleTrack>
            {showLabel && <Label>{isDarkMode ? 'Dark' : 'Light'}</Label>}
        </Container>
    );
};

const Container = styled.button`
    display: flex;
    align-items: center;
    gap: 0.8rem;
    background: none;
    border: none;
    cursor: pointer;
    padding: 0.5rem;
    border-radius: 0.5rem;
    transition: background-color 0.2s ease;

    &:hover {
        background-color: ${props => props.theme.colors.subtle.lightHover};
    }

    &:focus-visible {
        outline: 2px solid ${props => props.theme.colors.primary};
        outline-offset: 2px;
    }
`;

const ToggleTrack = styled.div<{ $isDark: boolean }>`
    position: relative;
    width: 4.8rem;
    height: 2.4rem;
    background: ${props => props.theme.colors.button.medium};
    border-radius: 1.2rem;
    transition: background-color 0.3s ease;
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 0 0.4rem;
`;

const IconWrapper = styled.span<{ $position: 'left' | 'right'; $isActive: boolean }>`
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 1;
    color: ${props =>
        props.$isActive ? props.theme.colors.primary : props.theme.colors.text.primary};
    transition: color 0.3s ease, opacity 0.3s ease;
`;

const ToggleThumb = styled.div<{ $isDark: boolean }>`
    position: absolute;
    top: 0.2rem;
    left: ${props => (props.$isDark ? 'calc(100% - 2.2rem)' : '0.2rem')};
    width: 2rem;
    height: 2rem;
    background: ${props => props.theme.colors.primary};
    border-radius: 50%;
    transition: left 0.3s cubic-bezier(0.68, -0.55, 0.265, 1.55);
    box-shadow: 0 0.2rem 0.4rem rgba(0, 0, 0, 0.2);
`;

const Label = styled.span`
    font-size: ${props => props.theme.text.size.small};
    color: ${props => props.theme.colors.text.primary};
    min-width: 3.5rem;
`;

export default ThemeToggle;
